import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";

import {
  fetchQualityCustomerQuickSearch,
  type QualityCustomerSearchRow,
} from "./rmaApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

export function QualityCustomerPicker({
  id,
  value,
  knownLabel,
  disabled,
  onChange,
}: {
  id: string;
  value: number | null;
  knownLabel?: string | null;
  disabled?: boolean;
  onChange: (fsAccountId: number | null) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityCustomerSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = value != null ? String(value) : "";
  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "customer-quick-search", searchQuery],
    queryFn: () => fetchQualityCustomerQuickSearch(searchQuery),
    enabled: searchQuery.length >= 2 || (value != null && searchQuery === lookupQuery),
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) return;
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.fsAccountId, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRow = value != null ? knownRows.get(value) ?? null : null;
  const selectedValues = useMemo(
    () => (value != null ? new Set([String(value)]) : new Set<string>()),
    [value],
  );

  const options = useMemo((): SearchableSelectOption[] => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualityCustomerSearchRow>();
    for (const row of rows) {
      byId.set(row.fsAccountId, row);
    }
    if (selectedRow != null) {
      byId.set(selectedRow.fsAccountId, selectedRow);
    } else if (value != null) {
      byId.set(value, {
        fsAccountId: value,
        name: knownLabel?.trim() || `Account ${value}`,
        endUserName: null,
      });
    }
    return [...byId.values()].map((row) => ({
      value: String(row.fsAccountId),
      label: row.name?.trim() || `Account ${row.fsAccountId}`,
      description: null,
    }));
  }, [searchResultsQuery.data?.rows, selectedRow, value, knownLabel]);

  return (
    <SearchableMultiSelect
      id={id}
      variant="chips"
      alwaysShowChips
      maxSelected={1}
      selectedValues={selectedValues}
      onSelectedValuesChange={(next) => {
        const ids = [...next];
        if (ids.length === 0) {
          onChange(null);
          setSearchValue("");
          return;
        }
        const parsed = Number(ids[ids.length - 1]);
        if (!Number.isFinite(parsed) || parsed < 1) return;
        onChange(parsed);
        setSearchValue("");
      }}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder="Search customers and end users"
      searchPlaceholder="Search account name"
      emptyMessage={
        searchValue.trim().length < 2
          ? "Type at least two characters to search accounts."
          : "No accounts match."
      }
      isLoading={searchResultsQuery.isFetching}
      disabled={disabled}
      panelMinWidth={320}
    />
  );
}
